import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { parseProfileUrl, processWebhooks } from '@/utils/webhook';
import { InspirationSource } from '@/types/webhook';

interface SourceLimits {
  maxSources: number;
  currentSources: number;
  remainingSlots: number;
  canAddMore: boolean;
}

interface GenerationStatus {
  isGenerating: boolean;
  lastGeneration: Date | null;
}

interface InspirationSourceRow {
  id: string;
  username: string;
  platform: string;
  created_at: string;
  scraping_status: string | null;
  total_posts_scraped: number | null;
  last_successful_scrape: string | null;
}

export const useInspirationSources = (userId: string | null, maxSources: number = 5) => {
  const [sources, setSources] = useState<InspirationSourceRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState({ 
    completed: 0, 
    total: 0, 
    currentSource: null as InspirationSource | null 
  });
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  /**
   * Fetch user's inspiration sources
   */
  const fetchSources = useCallback(async () => {
    if (!userId) return;

    try {
      setIsLoading(true);
      
      // First get the inspiration sources
      const { data: sourcesData, error: sourcesError } = await supabase
        .from('inspiration_sources')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (sourcesError) {
        console.error('Fetch sources error:', sourcesError);
        setError('Failed to load inspiration sources');
        return;
      }

      // Then get scraped content counts for each source
      const sourcesWithStats = await Promise.all(
        (sourcesData || []).map(async (source) => {
          // Count posts scraped by FK or by username (handles legacy rows without FK)
          const { count: postsCount } = await supabase
            .from('scraped_content')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', userId)
            .or(`inspiration_source_id.eq.${source.id},username.eq.${source.username}`);

          // Determine actual scraping status based on data
          let actualStatus = 'pending';
          if (postsCount && postsCount > 0) {
            actualStatus = 'completed';
          } else if (source.scraping_status === 'processing') {
            actualStatus = 'processing';
          }

          return {
            ...source,
            total_posts_scraped: postsCount || 0,
            last_successful_scrape: null,
            scraping_status: actualStatus
          };
        })
      );

      setSources(sourcesWithStats);
      setError(null);
    } catch (error) {
      console.error('Unexpected fetch error:', error);
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  /**
   * Get current source limits
   */
  const getSourceLimits = useCallback((): SourceLimits => {
    const currentCount = sources.length;
    return {
      maxSources: maxSources,
      currentSources: currentCount,
      remainingSlots: maxSources - currentCount,
      canAddMore: currentCount < maxSources
    };
  }, [sources.length, maxSources]);

  /**
   * Validate source limit before adding
   */
  const validateSourceLimit = useCallback((): { canAdd: boolean; remaining: number; current: number } => {
    const currentCount = sources.length;
    return {
      canAdd: currentCount < maxSources,
      remaining: maxSources - currentCount,
      current: currentCount
    };
  }, [sources.length, maxSources]);

  /**
   * Add a new inspiration source
   */
  const addSource = useCallback(async (url: string): Promise<{ success: boolean; error?: string }> => {
    if (!userId) {
      throw new Error('User not authenticated');
    }

    const limits = validateSourceLimit();
    if (!limits.canAdd) {
      throw new Error(`Osiągnięto limit ${maxSources} źródeł inspiracji. Usuń jedno, aby dodać nowe.`);
    }

    // Parse and validate URL
    const parsedSource = parseProfileUrl(url);
    if (!parsedSource) {
      throw new Error('Invalid URL format. Please enter a valid Instagram, TikTok, or YouTube URL.');
    }

    // Check if source already exists
    const existingSource = sources.find(s => s.username === parsedSource.username && s.platform === parsedSource.platform);
    if (existingSource) {
      throw new Error(`@${parsedSource.username} is already in your sources`);
    }

    // Add to database with proper error handling
    const { data, error: insertError } = await supabase
      .from('inspiration_sources')
      .insert([{
        user_id: userId,
        username: parsedSource.username,
        platform: parsedSource.platform,
        scraping_status: 'pending'
      }])
      .select()
      .single();

    if (insertError) {
      console.error('Insert source error:', insertError);
      // Check if it's a limit error from the database trigger
      if (insertError.message.includes('źródeł inspiracji') || insertError.message.includes('inspiration sources')) {
        throw new Error(`Osiągnięto limit źródeł inspiracji dla Twojego planu. Usuń jedno, aby dodać nowe.`);
      }
      if (insertError.message.includes('Miesięczny limit') || insertError.message.includes('Monthly limit')) {
        throw new Error('Wykorzystano limit dodań źródeł w tym miesiącu. Spróbuj ponownie w następnym okresie.');
      }
      throw new Error('Nie udało się dodać źródła do bazy danych');
    }

    // Refresh sources to show the new one
    await fetchSources();

    // Trigger content generation in background
    processWebhooks(userId, [parsedSource], (completed, total, currentSource, isProcessing) => {
      setGenerationProgress({ completed, total, currentSource });
      if (isProcessing) {
        setIsGenerating(true);
      } else if (completed === total) {
        setIsGenerating(false);
      }
    }).catch(error => {
      console.error('Webhook processing error:', error);
    });

    return { success: true };
  }, [userId, sources, validateSourceLimit, fetchSources]);

  /**
   * Remove an inspiration source
   * Note: Scraped content and generated ideas are preserved
   */
  const removeSource = useCallback(async (sourceId: string): Promise<{ success: boolean; error?: string }> => {
    if (!userId) {
      throw new Error('User not authenticated');
    }

    const sourceToRemove = sources.find(s => s.id === sourceId);
    if (!sourceToRemove) {
      throw new Error('Source not found');
    }

    // Delete from database - scraped content will be preserved (FK set to NULL)
    const { error: deleteError } = await supabase
      .from('inspiration_sources')
      .delete()
      .eq('id', sourceId)
      .eq('user_id', userId);

    if (deleteError) {
      console.error('Delete source error:', deleteError);
      throw new Error('Failed to remove source');
    }

    // Refresh sources
    await fetchSources();

    return { success: true };
  }, [userId, sources, fetchSources]);

  /**
   * Generate content from all sources
   */
  const generateContent = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!userId) {
      return { success: false, error: 'User not authenticated' };
    }

    if (sources.length === 0) {
      return { success: false, error: 'No sources available. Add some inspiration sources first.' };
    }

    // Convert sources to InspirationSource format
    const inspirationSources: InspirationSource[] = sources.map(source => ({
      username: source.username,
      platform: source.platform as 'instagram' | 'tiktok'
    }));

    try {
      setIsGenerating(true);
      setGenerationProgress({ completed: 0, total: sources.length, currentSource: null });

      // Use localStorage to track generation state
      localStorage.setItem('content_generation_in_progress', 'true');
      localStorage.setItem('content_generation_start', new Date().toISOString());

      const results = await processWebhooks(
        userId,
        inspirationSources,
        (completed, total, currentSource) => {
          setGenerationProgress({ completed, total, currentSource });
        }
      );

      localStorage.removeItem('content_generation_in_progress');

      if (results.successes.length === inspirationSources.length) {
        toast({
          title: "✨ Content generation started!",
          description: `Generating content from your ${sources.length} sources. Check back in 2-3 minutes for new ideas.`,
        });
        return { success: true };
      } else if (results.successes.length > 0) {
        toast({
          title: "Partial success",
          description: `${results.successes.length} of ${sources.length} sources processed successfully`,
          variant: "destructive"
        });
        return { success: true };
      } else {
        return { success: false, error: 'Failed to process any sources' };
      }
    } catch (error) {
      console.error('Generate content error:', error);
      localStorage.removeItem('content_generation_in_progress');
      return { success: false, error: 'An unexpected error occurred during generation' };
    } finally {
      setIsGenerating(false);
    }
  }, [userId, sources, toast]);

  /**
   * Check if generation is in progress
   */
  const getGenerationStatus = useCallback((): GenerationStatus => {
    const inProgress = localStorage.getItem('content_generation_in_progress') === 'true';
    const startTimeStr = localStorage.getItem('content_generation_start');
    const lastGeneration = startTimeStr ? new Date(startTimeStr) : null;

    return {
      isGenerating: inProgress || isGenerating,
      lastGeneration
    };
  }, [isGenerating]);

  // Initial fetch
  useEffect(() => {
    if (userId) {
      fetchSources();
    }
  }, [userId, fetchSources]);

  return {
    // Data
    sources,
    isLoading,
    error,
    
    // Limits
    getSourceLimits,
    validateSourceLimit,
    
    // Actions
    addSource,
    removeSource,
    generateContent,
    fetchSources,
    
    // Generation status
    isGenerating,
    generationProgress,
    getGenerationStatus
  };
};